import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { TranslocoService } from '@ngneat/transloco';
import { ConfirmationService } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { CompetenceModel } from 'projects/core/src/lib/models/competence.model';
import { OnlinetestModel } from 'projects/core/src/lib/models/onlinetest.model';
import { v4 as uuidv4 } from 'uuid';

interface Behavioranchor {
  id: string;
  content: string;
}

@Component({
  selector: 'app-onlinetest-competences',
  templateUrl: './onlinetest-competences.component.html',
  styleUrl: './onlinetest-competences.component.scss'
})
export class OnlinetestCompetencesComponent implements OnInit {

  @Input() onlinetest: OnlinetestModel = null;
  @Input('competences') all: Array<CompetenceModel> = []; // tslint:disable-line:no-input-rename
  @Output() setEdit = new EventEmitter<string>();

  public competencesFound: boolean;
  public editEntry: CompetenceModel = null;
  public submitting = false;
  public optionsCompetences: Array<any> = [];
  public inEdit = false;

  public form: UntypedFormGroup = this.fb.group({
    competence: new UntypedFormControl(null, [Validators.required]),
    behavioranchors: this.fb.array([])
  });

  public faPlus = faPlus;
  public faTrash = faTrash;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    public transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.ui.setFocus();
  }

  onNew(): void {
    if (this.auth.hasRight('admin.onlinetest.edit')) {
      this.editEntry = null;
      this.startEdit();
    }
  }

  onEdit(entry: any): void {
    if (this.auth.hasRight('admin.onlinetest.edit')) {
      this.editEntry = entry;
      this.startEdit();
    }
  }

  onCancel(): void {
    this.stopEdit();
    this.ui.hideMessage();
  }

  onDelete(competence: CompetenceModel): void {
    if (this.auth.hasRight('admin.onlinetest.edit')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: competence.getLang('name', this.transloco.getActiveLang()) }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteOnlinetestCompetence(this.onlinetest.id, competence.id).subscribe({
            next: (result: any) => {
              if (result.data) {
                this.onlinetest.deserialize(result.data);
                this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: competence.getLang('name', this.transloco.getActiveLang()) }), this.transloco.translate('common.deleted'));
              }
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  onSubmit(): void {
    if (this.onlinetest !== null) {
      this.ui.hideMessage();
      if (this.form.valid === true) {
        this.submitting = true;
        const payload = {
          competence: this.form.get('competence').value.id,
          behavioranchors: this.form.get('behavioranchors').value,
        };
        this.api.saveOnlinetestCompetence(this.onlinetest.id, payload).subscribe({
          next: (result: any) => {
            if (result.data) {
              this.onlinetest.deserialize(result.data);
              this.ui.toastSuccess(this.transloco.translate('onlinetest_basedata.competence_added'), this.transloco.translate('common.added'));
              this.stopEdit();
            }
          },
          error: (error: any) => {
            this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          }
        }).add(() => {
          this.submitting = false;
        });
      } else {
        this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
        FormHelper.markFormAsDirty(this.form);
      }
    }
  }

  startEdit(): void {
    this.inEdit = true;
    this.setupForm();
    this.setEdit.emit('competences');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.clearForm();
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  private setupForm(): void {
    this.clearForm();
    this.optionsCompetences = [];
    if (this.editEntry !== null) {
      this.optionsCompetences = [this.editEntry];
      this.form.get('competence').setValue(this.editEntry);
      this.form.get('competence').disable();
      this.editEntry.pivot?.behavioranchors?.forEach((behavioranchor: any) => {
        this.addBehavioranchorEntry(behavioranchor.id, behavioranchor.content);
      });
      if ((this.form.get('behavioranchors') as UntypedFormArray).length === 0) {
        this.addBehavioranchorEntry();
      }
    } else {
      this.form.get('competence').enable();
      this.all.forEach((competence: CompetenceModel) => {
        if (this.onlinetest.competences.find((actual: any) => actual.id === competence.id) === undefined) {
          this.optionsCompetences.push(competence);
        }
      });
      this.addBehavioranchorEntry();
    }
  }

  private clearForm(): void {
    this.form.reset();
    this.optionsCompetences = [];
    this.form.patchValue({
      competence: null,
    });
    this.clearBehavioranchorEntries();
  }

  // input handling

  private clearBehavioranchorEntries(): void {
    const entries = this.form.get('behavioranchors') as UntypedFormArray;
    while (entries.length > 0) {
      entries.removeAt(0);
    }
  }

  public addBehavioranchorEntry(id: string = null, data: string = null): void {
    if (this.onlinetest.with_behavioranchors === true) {
      const entries = this.form.get('behavioranchors') as UntypedFormArray;
      entries.push(this.createBehavioranchorEntry(id, data));
    }
  }

  public deleteBehavioranchorEntry(index: number): void {
    const entries = this.form.get('behavioranchors') as UntypedFormArray;
    if (entries.length > 0) {
      entries.removeAt(index);
    }
  }

  private createBehavioranchorEntry(id: string = null, data: string = null): UntypedFormGroup {
    if (id === null) {
      id = uuidv4();
    }
    return this.fb.group({
      id: new UntypedFormControl(id, [Validators.required]),
      content: new UntypedFormControl(data, [Validators.required])
    });
  }

}
